import {
  aiJobErrorCodeSchema,
  contrastRatio,
  STORE_TYPEFACES,
  TEMPLATE_DEFAULTS,
  updateStoreDesignRequestSchema,
  type StoreColors,
  type StoreDesignView,
  type StoreTemplate,
  type StoreTypeface,
} from '@avrahami/contracts';
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  fill,
  formatDateTime,
  isApiError,
  Loading,
  NO_ERRORS,
  PageHeader,
  returnToPrevious,
  TextField,
  useVersionedDraft,
  type FieldErrors,
} from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { keys, platformApi, tenantDesignQuery, tenantQuery } from '../api/platform';
import { hasCapability, sessionQuery } from '../api/session';
import { FormStatus, StaleNotice } from '../components/forms';
import { TemplatePicker } from '../components/StoreTemplates';
import { he } from '../i18n/he';
import { mergeErrors, parseWith } from '../lib/forms';
import { errorMessage } from '../lib/format';
import { MIN_BRAND_CONTRAST, suggestPalettes } from '../lib/logo-palette';

const words = he.storeDesign;
const HEX = /^#[0-9a-f]{6}$/i;
/** The logo is read at most this wide or high: enough for its colours, cheap to scan. */
const LOGO_SAMPLE_SIDE = 160;

interface DesignDraft {
  template: StoreTemplate;
  typeface: StoreTypeface;
  /** As typed: a colour field may hold a half-written value. */
  colors: StoreColors;
}

const toDraft = (design: StoreDesignView): DesignDraft => ({
  template: design.template,
  typeface: design.typeface,
  colors: design.colors,
});

/**
 * A store's design, set by the platform staff (adr/0034, adr/0044): the template, the colours
 * (the template's own, taken from the store's logo, or set by hand) and the type, with a small
 * preview, and the opening banner the AI writes for the store. The corners are the template's.
 * The store's owner keeps the logo, the favicon and the banner's words in the store's own admin.
 */
export function TenantDesignPage() {
  const { tenantId } = useParams({ from: '/app/tenants/$tenantId/design' });
  const session = useQuery(sessionQuery);
  const can = (
    capability: 'platform.tenants.read' | 'platform.tenants.create' | 'platform.ai.manage',
  ) => session.data !== undefined && hasCapability(session.data, capability);
  const canRead = can('platform.tenants.read');
  const canManage = can('platform.tenants.create');
  // Only for the store's name in the title: the screen works without it.
  const tenant = useQuery({ ...tenantQuery(tenantId), enabled: canRead });
  const design = useQuery({ ...tenantDesignQuery(tenantId), enabled: canRead });
  const name = tenant.data?.tenant.displayName;
  const storeUrl = design.data?.storeUrl ?? null;
  return (
    <>
      <Link
        to="/tenants/$tenantId"
        params={{ tenantId }}
        onClick={returnToPrevious}
        className="mb-2 inline-block text-sm text-brand-700 hover:underline"
      >
        {words.back}
      </Link>
      <PageHeader
        title={name === undefined ? words.title : `${words.title}: ${name}`}
        actions={
          storeUrl === null ? null : (
            <a
              href={storeUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="design-view-store"
              className="text-sm font-medium text-brand-700 hover:underline"
            >
              {words.view}
            </a>
          )
        }
      />
      {canRead ? (
        <div className="flex flex-col gap-6">
          <p className="text-sm text-slate-600">{words.intro}</p>
          {canManage ? null : <Alert tone="info">{words.readOnly}</Alert>}
          {design.isPending ? <Loading /> : null}
          {design.isError ? (
            <Alert>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span>
                  {errorMessage(design.error, {
                    FORBIDDEN: words.forbidden,
                    NOT_FOUND: words.notFound,
                  })}
                </span>
                <Button
                  tone="secondary"
                  busy={design.isFetching}
                  onClick={() => {
                    void design.refetch();
                  }}
                >
                  {he.common.retry}
                </Button>
              </div>
            </Alert>
          ) : null}
          {design.data === undefined ? null : (
            <>
              <DesignForm
                key={tenantId}
                tenantId={tenantId}
                design={design.data}
                canManage={canManage}
                onReload={() => design.refetch().then((result) => result.data)}
              />
              <HeroCard
                tenantId={tenantId}
                design={design.data}
                canRewrite={can('platform.ai.manage')}
              />
            </>
          )}
        </div>
      ) : (
        <Alert>{words.forbidden}</Alert>
      )}
    </>
  );
}

function DesignForm({
  tenantId,
  design,
  canManage,
  onReload,
}: {
  tenantId: string;
  design: StoreDesignView;
  canManage: boolean;
  onReload: () => Promise<StoreDesignView | undefined>;
}) {
  const queryClient = useQueryClient();
  const editor = useVersionedDraft(design, (value) => value.version, toDraft);
  const [localErrors, setLocalErrors] = useState<FieldErrors>(NO_ERRORS);
  const save = useMutation({
    mutationFn: (draft: DesignDraft) =>
      platformApi.updateStoreDesign(tenantId, {
        ...draft,
        corners: TEMPLATE_DEFAULTS[draft.template].corners,
        version: editor.baseVersion,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(keys.tenantDesign(tenantId), updated);
      editor.reset(updated);
      void queryClient.invalidateQueries({ queryKey: keys.audit(tenantId) });
    },
    onError: (error) => {
      // The row behind the form is read again at once; the draft waits for the "reload" button.
      if (isApiError(error, 'CONFLICT')) void onReload();
    },
  });
  const errors = mergeErrors(localErrors, save.error);
  const { draft } = editor;
  const disabled = !canManage || save.isPending;
  const change = (next: Partial<DesignDraft>) => {
    editor.set(next);
    setLocalErrors(NO_ERRORS);
    if (!save.isIdle) save.reset();
  };
  const setColor = (key: keyof StoreColors, value: string) => {
    change({ colors: { ...draft.colors, [key]: value } });
  };
  const reload = () => {
    save.reset();
    setLocalErrors(NO_ERRORS);
    void onReload().then((latest) => {
      if (latest !== undefined) editor.reset(latest);
    });
  };
  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    const parsed = parseWith(updateStoreDesignRequestSchema, {
      ...draft,
      corners: TEMPLATE_DEFAULTS[draft.template].corners,
      version: editor.baseVersion,
    });
    if (!parsed.ok) {
      setLocalErrors(parsed.errors);
      return;
    }
    setLocalErrors(NO_ERRORS);
    save.mutate(parsed.value);
  };
  const validColors = HEX.test(draft.colors.brand) && HEX.test(draft.colors.accent);

  return (
    <form
      onSubmit={submit}
      noValidate
      className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]"
      data-testid="design-form"
    >
      <div className="flex min-w-0 flex-col gap-6">
        <StaleNotice stale={editor.stale && !save.isError} onReload={reload} />
        <Card title={words.template}>
          <div className="flex flex-col gap-3">
            <p className="text-sm text-slate-600">{words.templateHint}</p>
            <TemplatePicker
              value={draft.template}
              disabled={disabled}
              {...(validColors && HEX.test(draft.colors.ink) ? { colors: draft.colors } : {})}
              onChange={(template) => {
                // A template starts in the colours and type it was drawn in; they can be changed.
                const look = TEMPLATE_DEFAULTS[template];
                change({ template, colors: look.colors, typeface: look.typeface });
              }}
            />
            {draft.template !== design.template ? (
              <p className="text-sm text-slate-600" data-testid="template-look-applied">
                {words.templateLookApplied}
              </p>
            ) : null}
          </div>
        </Card>
        <Card title={words.colors}>
          <div className="flex flex-col gap-6">
            <LogoPalettes
              tenantId={tenantId}
              design={design}
              chosen={draft.colors}
              disabled={disabled}
              onPick={(colors) => {
                change({ colors });
              }}
            />
            <div className="grid gap-4 md:grid-cols-3">
              {(['brand', 'accent', 'ink'] as const).map((key) => (
                <ColorField
                  key={key}
                  name={key}
                  label={words[key]}
                  hint={words[`${key}Hint`]}
                  value={draft.colors[key]}
                  error={errors[`colors.${key}`]}
                  disabled={disabled}
                  onChange={(value) => {
                    setColor(key, value);
                  }}
                />
              ))}
            </div>
            <ContrastWarning brand={draft.colors.brand} />
          </div>
        </Card>
        <Card title={words.typeface}>
          <ChoiceGroup
            name="typeface"
            label={words.typeface}
            options={STORE_TYPEFACES}
            value={draft.typeface}
            disabled={disabled}
            onChange={(typeface) => {
              change({ typeface });
            }}
            render={(typeface) => (
              <span className="flex flex-col">
                <span className="font-medium">{words.typefaces[typeface]}</span>
                <span
                  className="text-lg text-slate-700"
                  style={{ fontFamily: FONT_FAMILIES[typeface] }}
                >
                  {words.typefaceSample}
                </span>
              </span>
            )}
          />
        </Card>
        <FormStatus
          error={save.error}
          localInvalid={Object.keys(localErrors).length > 0}
          saved={save.isSuccess}
          onReload={reload}
        />
        {save.isSuccess ? (
          <p className="sr-only" data-testid="design-saved">
            {words.saved}
          </p>
        ) : null}
        {canManage ? (
          <div>
            <Button
              type="submit"
              busy={save.isPending}
              disabled={!isDirty(draft, design)}
              data-testid="design-save"
            >
              {he.common.save}
            </Button>
          </div>
        ) : null}
      </div>
      <aside className="xl:sticky xl:top-6 xl:self-start">
        <Card title={words.preview}>
          <StorePreview draft={draft} address={design.storeUrl} />
        </Card>
      </aside>
    </form>
  );
}

function isDirty(draft: DesignDraft, design: StoreDesignView): boolean {
  const saved = toDraft(design);
  return (
    draft.template !== saved.template ||
    draft.typeface !== saved.typeface ||
    (['brand', 'accent', 'ink'] as const).some(
      (key) => draft.colors[key].toLowerCase() !== saved.colors[key],
    )
  );
}

/** The corners of each template as the preview draws them. */
const PREVIEW_RADIUS: Readonly<Record<StoreTemplate, string>> = {
  PANORAMA: '6px',
  ATELIER: '0',
  DEPOT: '0',
  FRESH: '14px',
  PLANNER: '8px',
};

/** The headings' font of each typeface, when this computer has it; the name says it anyway. */
const FONT_FAMILIES: Readonly<Record<StoreTypeface, string>> = {
  INDUSTRIAL: "'Secular One', sans-serif",
  MODERN: "'Rubik', sans-serif",
  CLASSIC: "'Frank Ruhl Libre', serif",
  HEEBO: "'Heebo', sans-serif",
  ASSISTANT: "'Assistant', sans-serif",
  NOTO: "'Noto Serif Hebrew', serif",
  SUEZ: "'Suez One', serif",
  KARANTINA: "'Karantina', sans-serif",
};

/** A colour as a picker and as hex text, both for one value. */
function ColorField({
  name,
  label,
  hint,
  value,
  error,
  disabled,
  onChange,
}: {
  name: string;
  label: string;
  hint: string;
  value: string;
  error: string | undefined;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const valid = HEX.test(value);
  return (
    <div className="flex items-start gap-2">
      <input
        type="color"
        aria-label={label}
        data-testid={`color-${name}-picker`}
        className="mt-6 h-10 w-12 shrink-0 cursor-pointer rounded border border-slate-300 bg-white p-0.5 disabled:cursor-not-allowed"
        value={valid ? value.toLowerCase() : '#000000'}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
      <TextField
        label={label}
        name={`color-${name}`}
        dir="ltr"
        maxLength={7}
        autoComplete="off"
        spellCheck={false}
        className="min-w-0 flex-1"
        // The code reads in a fixed-width font; the label and the hint stay as the others.
        style={{ fontFamily: 'ui-monospace, monospace' }}
        data-testid={`color-${name}`}
        hint={hint}
        value={value}
        error={error ?? (valid || value === '' ? undefined : words.hexInvalid)}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.target.value.trim());
        }}
      />
    </div>
  );
}

/** Says when the brand colour is too light for text on white. */
function ContrastWarning({ brand }: { brand: string }) {
  if (!HEX.test(brand)) return null;
  const ratio = contrastRatio(brand.toLowerCase(), '#ffffff');
  if (ratio >= MIN_BRAND_CONTRAST) return null;
  return (
    <div data-testid="contrast-warning">
      <Alert tone="warning">{fill(words.lowContrast, { ratio: ratio.toFixed(1) })}</Alert>
    </div>
  );
}

/** A radio group of a few options, each drawn by `render`. */
function ChoiceGroup<Option extends string>({
  name,
  label,
  options,
  value,
  disabled,
  onChange,
  render,
}: {
  name: string;
  label: string;
  options: readonly Option[];
  value: Option;
  disabled: boolean;
  onChange: (value: Option) => void;
  render: (option: Option) => ReactNode;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-col gap-2">
      {options.map((option) => {
        const chosen = option === value;
        return (
          <label
            key={option}
            data-testid={`${name}-${option}`}
            data-chosen={chosen}
            className={`flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 ring-1 focus-within:ring-2 focus-within:ring-brand-600 ${
              chosen ? 'bg-brand-50 ring-brand-600' : 'bg-white ring-slate-200 hover:bg-slate-50'
            } ${disabled ? 'cursor-not-allowed opacity-70' : ''}`}
          >
            <input
              type="radio"
              name={name}
              value={option}
              checked={chosen}
              disabled={disabled}
              className="h-4 w-4 accent-brand-600"
              onChange={() => {
                onChange(option);
              }}
            />
            {render(option)}
          </label>
        );
      })}
    </div>
  );
}

type LogoReading = { state: 'NONE' } | { state: 'READY'; palettes: StoreColors[] };

/** Reads the logo's pixels in a canvas: drawn small, which is plenty for its colours. */
async function readLogo(tenantId: string): Promise<StoreColors[]> {
  const blob = await platformApi.storeLogo(tenantId);
  const bitmap = await createImageBitmap(blob);
  try {
    const scale = Math.min(1, LOGO_SAMPLE_SIDE / Math.max(bitmap.width, bitmap.height, 1));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (context === null) throw new Error('no 2d canvas');
    context.drawImage(bitmap, 0, 0, width, height);
    return suggestPalettes(context.getImageData(0, 0, width, height).data);
  } finally {
    bitmap.close();
  }
}

/** "Colours from the logo": up to four palettes made from the store's logo, to pick from. */
function LogoPalettes({
  tenantId,
  design,
  chosen,
  disabled,
  onPick,
}: {
  tenantId: string;
  design: StoreDesignView;
  chosen: StoreColors;
  disabled: boolean;
  onPick: (colors: StoreColors) => void;
}) {
  const logo = design.logo;
  const reading = useQuery({
    queryKey: [...keys.tenantDesign(tenantId), 'logo', logo?.mediaId ?? 'none'],
    queryFn: async (): Promise<LogoReading> =>
      logo === null ? { state: 'NONE' } : { state: 'READY', palettes: await readLogo(tenantId) },
    staleTime: Infinity,
    retry: false,
  });
  const same = (palette: StoreColors) =>
    (['brand', 'accent', 'ink'] as const).every(
      (key) => palette[key] === chosen[key].toLowerCase(),
    );
  return (
    <section className="flex flex-col gap-3" data-testid="logo-palettes">
      <div>
        <h3 className="font-semibold text-slate-900">{words.fromLogo}</h3>
        <p className="text-sm text-slate-600">{words.fromLogoHint}</p>
      </div>
      {reading.isPending ? <p className="text-sm text-slate-600">{words.readingLogo}</p> : null}
      {reading.isError ? <Alert tone="warning">{words.logoFailed}</Alert> : null}
      {reading.data?.state === 'NONE' ? <Alert tone="info">{words.noLogo}</Alert> : null}
      {reading.data?.state === 'READY' && reading.data.palettes.length === 0 ? (
        <Alert tone="info">{words.logoColorless}</Alert>
      ) : null}
      {reading.data?.state === 'READY' && reading.data.palettes.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {reading.data.palettes.map((palette, index) => {
            const picked = same(palette);
            const label = fill(words.palette, { n: index + 1 });
            return (
              <li key={`${palette.brand}-${palette.accent}`}>
                <button
                  type="button"
                  disabled={disabled}
                  aria-pressed={picked}
                  aria-label={`${label}: ${palette.brand}, ${palette.accent}, ${palette.ink}`}
                  data-testid={`palette-${String(index)}`}
                  onClick={() => {
                    onPick(palette);
                  }}
                  className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-start ring-1 transition focus-visible:outline-2 focus-visible:outline-brand-700 disabled:cursor-not-allowed disabled:opacity-70 ${
                    picked
                      ? 'bg-brand-50 ring-2 ring-brand-600'
                      : 'bg-white ring-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <span className="flex overflow-hidden rounded ring-1 ring-slate-300">
                    {(['brand', 'accent', 'ink'] as const).map((key) => (
                      <span
                        key={key}
                        className="h-8 w-10"
                        style={{ backgroundColor: palette[key] }}
                        title={`${words[key]} ${palette[key]}`}
                      />
                    ))}
                  </span>
                  <span className="text-sm font-medium text-slate-900">{label}</span>
                  <span className="ltr font-mono text-xs text-slate-500">
                    {palette.brand} · {palette.accent} · {palette.ink}
                  </span>
                  {picked ? (
                    <span className="ms-auto text-xs font-medium text-brand-700">
                      {words.paletteChosen}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}

/** A small store in the chosen colours, corners and type: a header, a button and a card. */
function StorePreview({ draft, address }: { draft: DesignDraft; address: string | null }) {
  const color = (key: keyof StoreColors, fallback: string) =>
    HEX.test(draft.colors[key]) ? draft.colors[key] : fallback;
  const brand = color('brand', '#1b2855');
  const accent = color('accent', '#f59e0b');
  const ink = color('ink', '#171b21');
  const radius = PREVIEW_RADIUS[draft.template];
  const heading = FONT_FAMILIES[draft.typeface];
  return (
    <div
      className="overflow-hidden rounded-md bg-slate-50 ring-1 ring-slate-200"
      data-testid="design-preview"
      data-template={draft.template}
    >
      <div className="h-2" style={{ backgroundColor: ink }} />
      <div className="flex items-center justify-between gap-2 bg-white px-3 py-2">
        <span className="font-semibold" style={{ fontFamily: heading, color: ink }}>
          {words.previewStore}
        </span>
        <span className="ltr truncate text-[10px] text-slate-400">
          {address?.replace(/^https:\/\//, '') ?? ''}
        </span>
      </div>
      <div className="flex flex-col gap-3 p-3">
        <p className="text-lg leading-tight font-bold" style={{ fontFamily: heading, color: ink }}>
          {words.typefaceSample}
          <br />
          <span style={{ color: brand }}>{words.hero.highlight}</span>
        </p>
        <span
          className="self-start px-3 py-1.5 text-sm font-medium text-white"
          style={{ backgroundColor: brand, borderRadius: radius }}
          data-testid="design-preview-button"
        >
          {words.previewButton}
        </span>
        <div
          className="flex gap-3 bg-white p-2 ring-1 ring-slate-200"
          style={{ borderRadius: radius }}
        >
          <span
            className="h-12 w-12 shrink-0"
            style={{ backgroundColor: `${brand}26`, borderRadius: radius }}
          />
          <span className="flex flex-col gap-1">
            <span
              className="self-start px-1.5 text-[10px] font-semibold"
              style={{ backgroundColor: accent, color: ink, borderRadius: radius }}
            >
              {words.previewBadge}
            </span>
            <span className="text-sm" style={{ color: ink }}>
              {words.previewCard}
            </span>
          </span>
        </div>
      </div>
      <div className="h-6" style={{ backgroundColor: ink }} />
    </div>
  );
}

/**
 * The opening banner: its words as the store shows them now, where the AI stands with them, and
 * another version on request (it replaces words the owner may have edited, so it asks first).
 */
function HeroCard({
  tenantId,
  design,
  canRewrite,
}: {
  tenantId: string;
  design: StoreDesignView;
  canRewrite: boolean;
}) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const rewrite = useMutation({
    mutationFn: () => platformApi.regenerateStoreHero(tenantId),
    onSuccess: async () => {
      setConfirming(false);
      await queryClient.invalidateQueries({ queryKey: keys.tenantDesign(tenantId) });
      void queryClient.invalidateQueries({ queryKey: keys.audit(tenantId) });
    },
  });
  const { hero, heroJob } = design;
  const writing = heroJob.state === 'QUEUED' || heroJob.state === 'RUNNING';
  const code = aiJobErrorCodeSchema.safeParse(heroJob.error).data;
  // The banner's own reasons where the copy's words would mislead (no product here).
  const reason =
    code === undefined
      ? he.aiCopy.jobErrorUnknown
      : code === 'FACT_GUARD'
        ? words.hero.factGuard
        : code === 'TOO_SIMILAR'
          ? words.hero.banned
          : he.aiCopy.jobErrors[code];
  const rows: [string, string | null, string][] = [
    [words.hero.eyebrow, hero.eyebrow, 'eyebrow'],
    [words.hero.headline, hero.title, 'title'],
    [words.hero.highlight, hero.highlight, 'highlight'],
    [words.hero.text, hero.text, 'text'],
    [words.hero.buttonLabel, hero.buttonLabel, 'button'],
  ];
  return (
    <Card title={words.hero.title}>
      <div className="flex flex-col gap-4" data-testid="hero-card" data-state={heroJob.state}>
        <p className="text-sm text-slate-600">{words.hero.intro}</p>
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
          {rows.map(([label, value, key]) => (
            <div key={key} className="contents">
              <dt className="text-slate-500">{label}</dt>
              <dd
                data-testid={`hero-${key}`}
                className={value === null ? 'text-slate-400' : 'text-slate-900'}
              >
                {value ?? words.hero.empty}
              </dd>
            </div>
          ))}
        </dl>
        <div data-testid="hero-state">
          <Alert
            tone={
              heroJob.state === 'FAILED' ? 'error' : heroJob.state === 'DONE' ? 'success' : 'info'
            }
          >
            {fill(words.hero.states[heroJob.state], { reason })}
            {heroJob.finishedAt === null || writing ? null : (
              <span className="ms-2 text-xs text-slate-600">
                ({words.hero.finishedAt} {formatDateTime(heroJob.finishedAt)})
              </span>
            )}
          </Alert>
        </div>
        {rewrite.isSuccess && writing ? <Alert tone="success">{words.hero.queued}</Alert> : null}
        {rewrite.isError && !confirming ? <Alert>{errorMessage(rewrite.error)}</Alert> : null}
        {canRewrite ? (
          <div>
            <Button
              tone="secondary"
              busy={rewrite.isPending}
              disabled={writing}
              data-testid="hero-rewrite"
              onClick={() => {
                rewrite.reset();
                setConfirming(true);
              }}
            >
              {words.hero.rewrite}
            </Button>
          </div>
        ) : null}
        <ConfirmDialog
          open={confirming}
          title={words.hero.rewriteTitle}
          confirmLabel={words.hero.rewrite}
          confirmTestId="hero-rewrite-confirm"
          busy={rewrite.isPending}
          onConfirm={() => {
            rewrite.mutate();
          }}
          onClose={() => {
            setConfirming(false);
          }}
        >
          <div className="flex flex-col gap-3">
            <p>{words.hero.rewriteBody}</p>
            {rewrite.isError ? <Alert>{errorMessage(rewrite.error)}</Alert> : null}
          </div>
        </ConfirmDialog>
      </div>
    </Card>
  );
}
